import { useEffect, useRef } from "react";
import type { CareerMove } from "@career-ops-amplify/shared";
import { cn } from "@/cn";
import { formatDue, priorityLabel } from "./format";

export type MovesDensity = "compact" | "roomy";

type Props = {
  move: CareerMove;
  selected?: boolean;
  onSelect: () => void;
  density?: MovesDensity;
};

function PriorityBadge({
  priority,
  className,
}: {
  priority: CareerMove["priority"];
  className?: string;
}) {
  return (
    <span
      className={cn(
        "shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium leading-none",
        priority === "critical"
          ? "bg-red-500/15 text-red-400"
          : priority === "high"
            ? "bg-amber-500/15 text-amber-400"
            : "bg-muted text-muted-foreground",
        className,
      )}
    >
      {priorityLabel(priority)}
    </span>
  );
}

export function CareerMoveCard({
  move,
  selected,
  onSelect,
  density = "roomy",
}: Props) {
  const cardRef = useRef<HTMLButtonElement>(null);
  const compact = density === "compact";

  useEffect(() => {
    if (!selected) return;
    cardRef.current?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      behavior: "smooth",
    });
  }, [selected, move.id]);

  const meta = [move.companyName, move.roleTitle].filter(Boolean).join(" · ");

  return (
    <button
      ref={cardRef}
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full rounded-lg border text-left transition",
        compact ? "min-h-0 px-3 py-1.5" : "px-3.5 py-3",
        selected
          ? "border-brand/50 bg-brand/5"
          : "border-border bg-card hover:bg-muted/15",
      )}
    >
      {compact ? (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-5 text-foreground">
              {move.recommendedAction}
            </p>
            {meta ? (
              <p className="truncate text-xs leading-4 text-muted-foreground">
                {meta}
              </p>
            ) : null}
          </div>
          <PriorityBadge priority={move.priority} />
        </div>
      ) : (
        <>
          <p className="text-sm font-semibold text-foreground">
            {move.recommendedAction}
          </p>
          {meta ? (
            <p className="mt-1 truncate text-xs text-muted-foreground">{meta}</p>
          ) : null}
          <p className="mt-2 line-clamp-2 text-xs text-foreground/80">
            {move.whyNow}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            {move.dueAt ? <span>{formatDue(move.dueAt)}</span> : null}
            <PriorityBadge priority={move.priority} />
          </div>
        </>
      )}
    </button>
  );
}
